/*
Modified from original hosted here:
https://github.com/bewithjonam/mapboxgl-spiderifier

MIT License

Copyright (c) 2016 manoj kumar

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */
import mapboxgl from 'mapbox-gl';

// Utility

const eachTimes = (count, iterator) => {
  if (!count) {
    return;
  }
  for (let i = 0; i < count; i++) {
    iterator(i);
  }
};

const mapTimes = (count, iterator) => {
  const result = [];
  eachTimes(count, i => {
    result.push(iterator(i));
  });
  return result;
};

const offsetVariant = (offset, variantX, variantY) => {
  return [offset[0] + (variantX || 0), offset[1] + (variantY || 0)];
};

const twoPi = Math.PI * 2;

// Spiderifier
function MapboxglSpiderifier(map, userOptions) {
  const NULL_FUNCTION = () => {};
  const options = {
    animate: false, // to animate the spiral
    animationSpeed: 0, // animation speed in milliseconds
    customPin: false, // If false, sets a default icon for pins in spider legs.
    initializeLeg: NULL_FUNCTION,
    onClick: NULL_FUNCTION,
    // --- <SPIDER TUNING Params>
    // circleSpiralSwitchover: show spiral instead of circle from this marker count upwards
    //                        0 -> always spiral; Infinity -> always circle
    circleSpiralSwitchover: 9,
    circleFootSeparation: 25, // related to circumference of circle
    spiralFootSeparation: 28, // related to size of spiral (experiment!)
    spiralLengthStart: 15, // ditto
    spiralLengthFactor: 4 // ditto
    // ---
  };
  let previousSpiderLegs = [];

  Object.keys(userOptions).forEach(attrname => {
    options[attrname] = userOptions[attrname];
  });

  // Private:
  function generateSpiralParams(count) {
    let legLength = options.spiralLengthStart;
    let angle = 0;
    return mapTimes(count, index => {
      angle += options.spiralFootSeparation / legLength + index * 0.0005;
      const pt = {
        x: legLength * Math.cos(angle),
        y: legLength * Math.sin(angle),
        angle,
        legLength,
        index
      };
      legLength += (twoPi * options.spiralLengthFactor) / angle;
      return pt;
    });
  }

  function generateCircleParams(count) {
    const circumference = options.circleFootSeparation * (2 + count);
    const legLength = circumference / twoPi; // = radius from circumference
    const angleStep = twoPi / count;

    return mapTimes(count, index => {
      const angle = index * angleStep;
      return {
        x: legLength * Math.cos(angle),
        y: legLength * Math.sin(angle),
        angle,
        legLength,
        index
      };
    });
  }

  function generateSpiderLegParams(count) {
    if (count >= options.circleSpiralSwitchover) {
      return generateSpiralParams(count);
    }
    return generateCircleParams(count);
  }

  function createMarkerElements(spiderLegParam) {
    const containerElem = document.createElement('div');
    const pinElem = document.createElement('div');
    const lineElem = document.createElement('div');

    containerElem.className = `spider-leg-container${options.animate ? ' animate initial ' : ' '}`;
    lineElem.className = 'spider-leg-line';
    pinElem.className = `spider-leg-pin${options.customPin ? '' : ' default-spider-pin'}`;

    containerElem.appendChild(lineElem);
    containerElem.appendChild(pinElem);

    containerElem.style['margin-left'] = `${spiderLegParam.x}px`;
    containerElem.style['margin-top'] = `${spiderLegParam.y}px`;

    lineElem.style.height = `${spiderLegParam.legLength}px`;
    lineElem.style.transform = `rotate(${spiderLegParam.angle - Math.PI / 2}rad)`;

    return { container: containerElem, line: lineElem, pin: pinElem };
  }

  function unspiderfy() {
    previousSpiderLegs.reverse().forEach((spiderLeg, index) => {
      if (options.animate) {
        spiderLeg.elements.container.style.transitionDelay = `${(options.animationSpeed / 1000 / previousSpiderLegs.length) * index}s`;
        spiderLeg.elements.container.className += ' exit';
        setTimeout(() => {
          spiderLeg.mapboxMarker.remove();
        }, options.animationSpeed + 100); // Wait for 100ms more before clearing the DOM
      } else {
        spiderLeg.mapboxMarker.remove();
      }
    });
    previousSpiderLegs = [];
  }

  function spiderfy(latLng, features) {
    const spiderLegParams = generateSpiderLegParams(features.length);

    unspiderfy();

    const spiderLegs = features.map((feature, index) => {
      const spiderLegParam = spiderLegParams[index];
      const elements = createMarkerElements(spiderLegParam, feature);
      const mapboxMarker = new mapboxgl.Marker({ element: elements.container }).setLngLat(latLng);

      const spiderLeg = {
        feature,
        elements,
        mapboxMarker,
        param: spiderLegParam
      };

      options.initializeLeg(spiderLeg);

      elements.container.onclick = e => {
        options.onClick(e, spiderLeg);
      };

      return spiderLeg;
    });

    const reversedSpiderLegs = spiderLegs.reverse();

    reversedSpiderLegs.forEach(spiderLeg => {
      spiderLeg.mapboxMarker.addTo(map);
    });

    if (options.animate) {
      reversedSpiderLegs.forEach((spiderLeg, index) => {
        spiderLeg.elements.container.className = (spiderLeg.elements.container.className || '').replace('initial', '');
        spiderLeg.elements.container.style.transitionDelay = `${(options.animationSpeed / 1000 / spiderLegs.length) * index}s`;
      });
    }

    previousSpiderLegs = spiderLegs;
  }

  // Public:
  this.spiderfy = spiderfy;
  this.unspiderfy = unspiderfy;
  this.spiderLegs = previousSpiderLegs;
}

// Returns Offset option for mapbox poup, so that the popup for pins in the spider
// appears next to the pin, rather than at the center of the spider.
// offset: <number> Offset of the popup from the pin.
MapboxglSpiderifier.popupOffsetForSpiderLeg = (spiderLeg, offset) => {
  const pinOffsetX = spiderLeg.param.x;
  const pinOffsetY = spiderLeg.param.y;

  offset = offset || 0;
  return {
    top: offsetVariant([0, offset], pinOffsetX, pinOffsetY),
    'top-left': offsetVariant([offset, offset], pinOffsetX, pinOffsetY),
    'top-right': offsetVariant([-offset, offset], pinOffsetX, pinOffsetY),
    bottom: offsetVariant([0, -offset], pinOffsetX, pinOffsetY),
    'bottom-left': offsetVariant([offset, -offset], pinOffsetX, pinOffsetY),
    'bottom-right': offsetVariant([-offset, -offset], pinOffsetX, pinOffsetY),
    left: offsetVariant([offset, -offset], pinOffsetX, pinOffsetY),
    right: offsetVariant([-offset, -offset], pinOffsetX, pinOffsetY)
  };
};

export default MapboxglSpiderifier;
